import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { AppShell } from "@/components/AppShell";
import { Autocomplete } from "@/components/Autocomplete";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import { formatAuftragLookupLabel } from "@/features/globalUpload/formatAuftragLookupLabel";
import { ApiError } from "@/lib/apiClient";
import { isElectronWithOpenFolder } from "@/lib/electron";
import { getRecentAuftraege, searchAuftraege, type AuftragLookup } from "@/lib/material";
import { toast } from "@/lib/toast";
import {
  assignTuerwartungProject,
  emptyDoorProtocolProgress,
  formatSkipReasonSummary,
  getProjectDoorProtocolStatus,
  isUnassignedProjectSummaryRow,
  listTuerwartungProjectsSummary,
  projectFilterLabel,
  resolveProtocolMeta,
  shouldShowNote5DominantHint,
  startProjectDoorProtocolGeneration,
  watchDoorProtocolJob,
  type DoorProtocolProgress,
  type DoorProtocolStatus,
  type TuerwartungProjectSummary,
} from "@/lib/tuerwartung";

const FILTER_ALL = "__all__";

function formatDateTime(value?: string | null): string {
  const raw = String(value || "").trim();
  if (!raw) return "–";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, "de", { sensitivity: "base", numeric: true }),
  );
}

async function openProtocolFolder(localPath: string, fallbackUrl: string): Promise<void> {
  const path = localPath.trim();
  const url = fallbackUrl.trim();

  if (!path && url) {
    window.open(url, "_blank", "noopener");
    return;
  }
  if (!path) {
    toast.error("Kein auflösbarer Ordnerpfad vorhanden.");
    return;
  }

  if (isElectronWithOpenFolder()) {
    const err = await window.envAPI!.openFolder!(path);
    if (!err) return;
    if (url) {
      window.open(url, "_blank", "noopener");
      return;
    }
    toast.error(`Ordner konnte nicht geöffnet werden: ${err}`);
    return;
  }

  if (url) {
    window.open(url, "_blank", "noopener");
    return;
  }
  toast.error("Ordner kann nur in der Desktop-App geöffnet werden.");
}

function FilterChips({
  label,
  options,
  selected,
  onSelect,
}: {
  label: string;
  options: string[];
  selected: string;
  onSelect: (value: string) => void;
}) {
  if (options.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium uppercase tracking-wide text-label-tertiary">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          className={clsx(
            "rounded-full px-3 py-1 text-sm font-medium transition",
            selected === FILTER_ALL
              ? "bg-accent text-label-on-accent"
              : "bg-fill-tertiary text-label-secondary hover:bg-fill-secondary hover:text-label",
          )}
          onClick={() => onSelect(FILTER_ALL)}
        >
          Alle
        </button>
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            className={clsx(
              "rounded-full px-3 py-1 text-sm font-medium transition",
              selected === opt
                ? "bg-accent text-label-on-accent"
                : "bg-fill-tertiary text-label-secondary hover:bg-fill-secondary hover:text-label",
            )}
            onClick={() => onSelect(opt)}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export function BrandschutztuerwartungPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const projectFromUrl = String(searchParams.get("project") || "").trim();

  const [projects, setProjects] = useState<TuerwartungProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [customer, setCustomer] = useState(FILTER_ALL);
  const [location, setLocation] = useState(FILTER_ALL);
  const [year, setYear] = useState(FILTER_ALL);
  const [selectedCode, setSelectedCode] = useState(projectFromUrl);
  const [onlyUnassigned, setOnlyUnassigned] = useState(false);

  const [protocolStatus, setProtocolStatus] = useState<DoorProtocolStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<DoorProtocolProgress>(emptyDoorProtocolProgress);
  const abortRef = useRef<AbortController | null>(null);

  const [assignTarget, setAssignTarget] = useState<TuerwartungProjectSummary | null>(null);
  const [assignSelection, setAssignSelection] = useState<AuftragLookup | null>(null);
  const [assignSaving, setAssignSaving] = useState(false);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listTuerwartungProjectsSummary();
      setProjects(rows);
    } catch (err) {
      setProjects([]);
      setError(err instanceof ApiError ? err.message : "Projektübersicht konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (!projectFromUrl) return;
    setSelectedCode(projectFromUrl);
  }, [projectFromUrl]);

  const selectedProject = useMemo(
    () => projects.find((row) => String(row.project_code || "").trim() === selectedCode) || null,
    [projects, selectedCode],
  );

  const customerOptions = useMemo(
    () => uniqueSorted(projects.flatMap((row) => row.assigned_customers || [])),
    [projects],
  );
  const locationOptions = useMemo(
    () => uniqueSorted(projects.flatMap((row) => row.assigned_locations || [])),
    [projects],
  );
  const yearOptions = useMemo(
    () => uniqueSorted(projects.flatMap((row) => row.assigned_years || [])),
    [projects],
  );

  const unassignedProjects = useMemo(
    () => projects.filter((row) => isUnassignedProjectSummaryRow(row)),
    [projects],
  );

  const filteredProjects = useMemo(() => {
    const q = query.trim().toLowerCase();
    return projects.filter((row) => {
      if (onlyUnassigned && !isUnassignedProjectSummaryRow(row)) return false;
      if (customer !== FILTER_ALL && !(row.assigned_customers || []).includes(customer)) return false;
      if (location !== FILTER_ALL && !(row.assigned_locations || []).includes(location)) return false;
      if (year !== FILTER_ALL && !(row.assigned_years || []).includes(year)) return false;
      if (!q) return true;
      const haystack = [
        row.project_code,
        row.project_raw,
        row.kunde,
        row.assigned_project_label,
        ...(row.assigned_customers || []),
        ...(row.assigned_locations || []),
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return haystack.includes(q);
    });
  }, [projects, customer, location, year, query, onlyUnassigned]);

  function openAssignDialog(row: TuerwartungProjectSummary) {
    setAssignTarget(row);
    setAssignSelection(null);
  }

  function closeAssignDialog() {
    if (assignSaving) return;
    setAssignTarget(null);
    setAssignSelection(null);
  }

  async function saveProjectAssignment() {
    const code = String(assignTarget?.project_code || "").trim();
    if (!code || !assignSelection?.id || assignSaving) return;

    setAssignSaving(true);
    try {
      const result = await assignTuerwartungProject(code, Number(assignSelection.id));
      const label = formatAuftragLookupLabel(assignSelection);
      setProjects((prev) =>
        prev.map((row) =>
          String(row.project_code || "").trim() === code
            ? {
                ...row,
                assigned_project_label: label,
                assigned_project_count: 1,
                assigned_customers: assignSelection.kunde
                  ? [String(assignSelection.kunde)]
                  : row.assigned_customers,
                assigned_locations: assignSelection.ort
                  ? [String(assignSelection.ort)]
                  : row.assigned_locations,
              }
            : row,
        ),
      );
      toast.success(`Projektzuordnung aktualisiert (${result.updated ?? 0}).`);
      setAssignTarget(null);
      setAssignSelection(null);
      await loadProjects();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Projektzuordnung konnte nicht gespeichert werden.");
    } finally {
      setAssignSaving(false);
    }
  }

  const loadStatus = useCallback(async (projectCode: string) => {
    const code = projectCode.trim();
    if (!code) {
      setProtocolStatus(null);
      setStatusError(null);
      return;
    }
    setStatusLoading(true);
    setStatusError(null);
    try {
      const status = await getProjectDoorProtocolStatus(code);
      setProtocolStatus(status);
    } catch (err) {
      setProtocolStatus(null);
      setStatusError(err instanceof ApiError ? err.message : "Protokollstatus konnte nicht geladen werden.");
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus(selectedCode);
  }, [selectedCode, loadStatus]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  function selectProject(code: string) {
    const next = code.trim();
    setSelectedCode(next);
    setProgress(emptyDoorProtocolProgress());
    const params = new URLSearchParams(searchParams);
    if (next) params.set("project", next);
    else params.delete("project");
    setSearchParams(params, { replace: true });
  }

  const protocolMeta = useMemo(
    () => resolveProtocolMeta(selectedProject, protocolStatus),
    [selectedProject, protocolStatus],
  );

  async function startGeneration(options: {
    force: boolean;
    note5Only?: boolean;
    successMessage: string;
    errorMessage: string;
  }) {
    const code = selectedCode.trim();
    if (!code || busy) return;

    if (options.force) {
      const confirmed = window.confirm("Bestehende Protokolle werden überschrieben. Fortfahren?");
      if (!confirmed) return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setBusy(true);
    setProgress({
      ...emptyDoorProtocolProgress(),
      state: "running",
    });

    try {
      const start = await startProjectDoorProtocolGeneration(code, {
        force: options.force,
        note5_only: Boolean(options.note5Only),
      });
      const jobId = String(start.job_id || "").trim();
      if (!jobId) throw new Error("Job-ID fehlt in der Antwort.");

      let latest = emptyDoorProtocolProgress();
      await watchDoorProtocolJob(
        code,
        jobId,
        (next) => {
          latest = next;
          setProgress(next);
        },
        { signal: controller.signal },
      );

      await loadStatus(code);
      await loadProjects();

      const skipReasonSummary =
        latest.skip_reason_summary || formatSkipReasonSummary(latest.summary?.skip_reasons);
      const note5Hint = shouldShowNote5DominantHint(Boolean(options.note5Only), latest.summary)
        ? "Hinweis: Keine Protokolle erzeugt, weil die Gefahrenbeurteilung überwiegend nicht Note 5 ist. Bitte Filter oder Datengrundlage prüfen."
        : "";

      if (skipReasonSummary || note5Hint) {
        setProgress((prev) => ({
          ...prev,
          skip_reason_summary: skipReasonSummary,
          message: note5Hint,
          state: "done",
        }));
      }

      const toastParts = [options.successMessage];
      if (skipReasonSummary) toastParts.push(`Übersprungene Gründe: ${skipReasonSummary}`);
      if (note5Hint) toastParts.push(note5Hint);
      toast.success(toastParts.join(" "));
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      const message = err instanceof Error ? err.message : options.errorMessage;
      setProgress((prev) => ({
        ...prev,
        state: "failed",
        message,
      }));
      toast.error(message || options.errorMessage);
    } finally {
      setBusy(false);
      if (abortRef.current === controller) abortRef.current = null;
    }
  }

  const forceRegenerate = !protocolMeta.canGenerateMissing;
  const canAct = Boolean(selectedCode) && !busy && protocolMeta.canRegenerateAll;
  const canOpenProtocolFolder = Boolean(
    protocolStatus?.protocols_folder_path || protocolStatus?.protocols_folder_url,
  );
  const canOpenNote5Folder = Boolean(
    protocolStatus?.note5_protocols_folder_path || protocolStatus?.note5_protocols_folder_url,
  );
  const showFolderButtons = Boolean(selectedCode) && (canOpenProtocolFolder || canOpenNote5Folder);

  return (
    <AppShell
      title="Brandschutztürwartung"
      subtitle="Wartungsprotokolle pro Projekt erzeugen und in SharePoint ablegen."
      actions={
        <Link
          to="/wartungen"
          className="inline-flex h-10 items-center rounded-lg bg-card px-4 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary"
        >
          Zur Übersicht
        </Link>
      }
    >
      {error ? (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      ) : null}

      {!loading && unassignedProjects.length > 0 ? (
        <Alert variant="warning" className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>
              {unassignedProjects.length} Wartungsprojekt
              {unassignedProjects.length === 1 ? "" : "e"} ohne zugewiesenen Auftrag.
            </span>
            <Button
              type="button"
              size="sm"
              variant={onlyUnassigned ? "primary" : "secondary"}
              onClick={() => setOnlyUnassigned((v) => !v)}
            >
              {onlyUnassigned ? "Alle Projekte zeigen" : "Nur ohne Zuordnung"}
            </Button>
          </div>
        </Alert>
      ) : null}

      <section className="grid gap-4 rounded-ios border border-separator bg-card p-4 shadow-ios lg:grid-cols-3">
        <FilterChips label="Kunde" options={customerOptions} selected={customer} onSelect={setCustomer} />
        <FilterChips label="Ort" options={locationOptions} selected={location} onSelect={setLocation} />
        <FilterChips label="Jahr" options={yearOptions} selected={year} onSelect={setYear} />
      </section>

      <div className="mt-4">
        <label className="block text-xs font-medium uppercase tracking-wide text-label-tertiary" htmlFor="bst-project-search">
          Projekt suchen
        </label>
        <input
          id="bst-project-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Projektnummer, Kunde, Ort …"
          className="mt-1.5 h-10 w-full max-w-xl rounded-lg border border-separator bg-card px-3 text-sm text-label outline-none ring-accent focus:ring-2"
        />
      </div>

      <p className="mt-3 text-sm text-label-secondary" aria-live="polite">
        {loading ? (
          <span className="inline-flex items-center gap-2">
            <Spinner /> Lade Projekte …
          </span>
        ) : (
          `${filteredProjects.length} Projekt${filteredProjects.length === 1 ? "" : "e"}${
            onlyUnassigned ? " ohne Zuordnung" : ""
          }`
        )}
      </p>

      <div className="mt-2 grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
        <section className="overflow-hidden rounded-ios border border-separator bg-card shadow-ios">
          <div className="max-h-[28rem] overflow-auto">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-fill-tertiary text-xs uppercase tracking-wide text-label-tertiary">
                <tr>
                  <th className="px-3 py-2 font-medium">Projekt</th>
                  <th className="px-3 py-2 font-medium">Kunde</th>
                  <th className="px-3 py-2 font-medium">Auftrag</th>
                  <th className="px-3 py-2 font-medium">Protokolle</th>
                  <th className="px-3 py-2 font-medium">Türen</th>
                  <th className="px-3 py-2 font-medium">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {!loading && filteredProjects.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-label-secondary">
                      Keine Projekte für die gewählte Filterkombination.
                    </td>
                  </tr>
                ) : null}
                {filteredProjects.map((row) => {
                  const code = String(row.project_code || "").trim();
                  const withProtocol = Number(row.maintenance_entries_with_protocol ?? row.protocols_total ?? 0);
                  const total = Number(row.maintenance_entries_total ?? row.visits_total ?? 0);
                  const selected = code === selectedCode;
                  const unassigned = isUnassignedProjectSummaryRow(row);
                  return (
                    <tr key={code || projectFilterLabel(row)}>
                      <td className="border-t border-separator px-3 py-2">
                        <button
                          type="button"
                          onClick={() => selectProject(code)}
                          className={clsx(
                            "w-full rounded-md px-2 py-1.5 text-left font-medium transition",
                            selected
                              ? "bg-accent-soft text-accent"
                              : "text-label hover:bg-fill-tertiary",
                          )}
                        >
                          {projectFilterLabel(row)}
                        </button>
                      </td>
                      <td className="border-t border-separator px-3 py-2 text-label-secondary">
                        {(row.assigned_customers || []).join(", ") || row.kunde || "–"}
                      </td>
                      <td className="border-t border-separator px-3 py-2 text-label-secondary">
                        {unassigned ? (
                          <span className="rounded-full bg-status-orange/15 px-2 py-0.5 text-xs text-status-orange">
                            nicht zugewiesen
                          </span>
                        ) : (
                          row.assigned_project_label || "–"
                        )}
                      </td>
                      <td className="border-t border-separator px-3 py-2 text-label-secondary">
                        {withProtocol}/{total}
                      </td>
                      <td className="border-t border-separator px-3 py-2 text-label-secondary">
                        {Number(row.doors_count || 0)}
                      </td>
                      <td className="border-t border-separator px-3 py-2">
                        {unassigned ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => openAssignDialog(row)}
                          >
                            Zuordnen
                          </Button>
                        ) : (
                          <span className="text-xs text-label-tertiary">–</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-ios border border-separator bg-card p-4 shadow-ios">
          <h2 className="text-base font-semibold text-label">Wartungsprotokolle</h2>
          {!selectedCode ? (
            <p className="mt-2 text-sm text-label-secondary">Bitte ein Projekt auswählen.</p>
          ) : (
            <>
              <p className="mt-1 text-sm text-label-secondary">
                Projekt <span className="font-medium text-label">{selectedCode}</span>
                {selectedProject?.assigned_project_label
                  ? ` · ${selectedProject.assigned_project_label}`
                  : ""}
              </p>

              {selectedProject && isUnassignedProjectSummaryRow(selectedProject) ? (
                <Alert variant="warning" className="mt-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span>Diesem Wartungsprojekt ist noch kein Auftrag zugeordnet.</span>
                    <Button type="button" size="sm" onClick={() => openAssignDialog(selectedProject)}>
                      Auftrag zuweisen
                    </Button>
                  </div>
                </Alert>
              ) : null}

              {statusError ? (
                <Alert variant="error" className="mt-3">
                  {statusError}
                </Alert>
              ) : null}

              <p className="mt-3 text-sm text-label-secondary" aria-live="polite">
                {statusLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <Spinner /> Lade Protokollstatus …
                  </span>
                ) : protocolMeta.maintenanceEntriesTotal > 0 ? (
                  `Protokolle: ${protocolMeta.maintenanceEntriesWithProtocol}/${protocolMeta.maintenanceEntriesTotal} · letzte Protokollerstellung: ${formatDateTime(protocolMeta.lastProtocolCreatedAt)}`
                ) : (
                  "Protokolle: Noch keine Wartungseinträge verfügbar."
                )}
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  type="button"
                  disabled={!canAct || statusLoading}
                  onClick={() =>
                    void startGeneration({
                      force: forceRegenerate,
                      successMessage: "Wartungsprotokolle erfolgreich erstellt.",
                      errorMessage: "Wartungsprotokolle konnten nicht erstellt werden.",
                    })
                  }
                >
                  {busy && progress.state === "running"
                    ? forceRegenerate
                      ? "Wartungsprotokolle werden neu generiert…"
                      : "Wartungsprotokolle werden erstellt…"
                    : forceRegenerate
                      ? "Wartungsprotokolle neu generieren"
                      : `Wartungsprotokolle erstellen (${protocolMeta.maintenanceEntriesMissingProtocol} offen)`}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!canAct || statusLoading}
                  onClick={() =>
                    void startGeneration({
                      force: false,
                      note5Only: true,
                      successMessage: "Note-5-Protokolle erfolgreich erstellt.",
                      errorMessage: "Note-5-Protokolle konnten nicht erstellt werden.",
                    })
                  }
                >
                  {busy && progress.state === "running"
                    ? "Wartungsprotokolle Note 5 werden erstellt…"
                    : "Wartungsprotokolle Note 5 generieren"}
                </Button>
              </div>

              {showFolderButtons ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {canOpenProtocolFolder &&
                  (protocolMeta.protocolCoverageComplete || protocolStatus?.protocols_folder_url) ? (
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={busy}
                      onClick={() =>
                        void openProtocolFolder(
                          String(protocolStatus?.protocols_folder_path || ""),
                          String(protocolStatus?.protocols_folder_url || ""),
                        )
                      }
                    >
                      Ordner Wartungsprotokolle öffnen
                    </Button>
                  ) : null}
                  {canOpenNote5Folder ? (
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={busy}
                      onClick={() =>
                        void openProtocolFolder(
                          String(protocolStatus?.note5_protocols_folder_path || ""),
                          String(protocolStatus?.note5_protocols_folder_url || ""),
                        )
                      }
                    >
                      Ordner Wartungsprotokolle Note 5 öffnen
                    </Button>
                  ) : null}
                </div>
              ) : null}

              {progress.state !== "idle" ? (
                <div
                  className={clsx(
                    "mt-4 rounded-lg border px-3 py-2 text-sm",
                    progress.state === "failed"
                      ? "border-status-red/40 bg-status-red/10 text-status-red"
                      : progress.state === "done"
                        ? "border-status-green/40 bg-status-green/10 text-label"
                        : "border-separator bg-fill-tertiary text-label-secondary",
                  )}
                  aria-live="polite"
                >
                  <p className="font-medium text-label">
                    {progress.state === "running"
                      ? "Generierung läuft…"
                      : progress.state === "done"
                        ? "Generierung abgeschlossen"
                        : "Generierung fehlgeschlagen"}
                  </p>
                  <p className="mt-1">
                    {progress.processed}/{progress.total || "–"} verarbeitet · {progress.generated} erzeugt ·{" "}
                    {progress.skipped} übersprungen · {progress.errors} Fehler
                    {progress.current_door ? ` · Tür ${progress.current_door}` : ""}
                  </p>
                  {progress.skip_reason_summary ? (
                    <p className="mt-1">Übersprungen: {progress.skip_reason_summary}</p>
                  ) : null}
                  {progress.message ? <p className="mt-1">{progress.message}</p> : null}
                </div>
              ) : null}
            </>
          )}
        </section>
      </div>

      <Dialog
        open={Boolean(assignTarget)}
        onClose={closeAssignDialog}
        title="Auftrag zuweisen"
        description={
          assignTarget
            ? `Wartungsprojekt ${String(assignTarget.project_code || "").trim() || "–"}${
                assignTarget.project_raw ? ` · ${assignTarget.project_raw}` : ""
              }`
            : undefined
        }
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" disabled={assignSaving} onClick={closeAssignDialog}>
              Abbrechen
            </Button>
            <Button
              type="button"
              loading={assignSaving}
              disabled={!assignSelection?.id}
              onClick={() => void saveProjectAssignment()}
            >
              Speichern
            </Button>
          </div>
        }
      >
        <Autocomplete<AuftragLookup>
          label="Auftrag"
          placeholder="Auftragsnummer, Ort oder Beschreibung …"
          hint="Alle Offpaper-Wartungen dieses Projektcodes werden dem gewählten Auftrag zugeordnet."
          minChars={0}
          loadOptions={async (q) => {
            const list = q.trim() ? await searchAuftraege(q) : await getRecentAuftraege({ limit: 15 });
            return list.map((a) => ({
              key: String(a.id),
              label: formatAuftragLookupLabel(a),
              description: a.beschreibung || undefined,
              value: a,
            }));
          }}
          onSelect={(opt) => setAssignSelection(opt?.value ?? null)}
        />
      </Dialog>
    </AppShell>
  );
}
